import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, Link } from "wouter";
import { authFetch } from "@/lib/authFetch";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, RefreshCw, GitMerge, EyeOff, Mail, Phone, Building2, CheckCircle2, AlertCircle, AlertTriangle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import type { Contact } from "@shared/schema";

type Reason = { type: "nom_fuzzy" | "nom_fuzzy_with_org" | "tel_match" | "email_match" | "email_similar"; score: number; detail: string };

type Candidate = {
  id: string;
  combinedScore: string;
  detectionReasons: Reason[];
  detectedAt: string;
  contactA: Contact & { organisationNom?: string; taskCount: number };
  contactB: Contact & { organisationNom?: string; taskCount: number };
};

function avatarInitials(prenom: string, nom: string) {
  return ((prenom?.[0] || "") + (nom?.[0] || "")).toUpperCase() || "?";
}
function avatarColor(name: string) {
  const colors = ["bg-blue-500","bg-emerald-500","bg-violet-500","bg-orange-500","bg-pink-500","bg-teal-500","bg-indigo-500","bg-amber-500"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function ReasonBadge({ reason }: { reason: Reason }) {
  const map: Record<string, { label: string; color: string }> = {
    nom_fuzzy: { label: `Nom à ${reason.score}%`, color: "border-violet-300 text-violet-700 dark:border-violet-700 dark:text-violet-400" },
    nom_fuzzy_with_org: { label: `Nom + Org (${reason.score}%)`, color: "border-violet-300 text-violet-700 dark:border-violet-700 dark:text-violet-400" },
    tel_match: { label: "Téléphone commun", color: "border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-400" },
    email_match: { label: "Email identique", color: "border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-400" },
    email_similar: { label: `Email à ${reason.score}%`, color: "border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-400" },
  };
  const { label, color } = map[reason.type] || { label: reason.type, color: "" };
  return (
    <Badge variant="outline" className={`text-[10px] h-4 px-1.5 ${color}`} title={reason.detail}>
      {label}
    </Badge>
  );
}

function ContactMiniCard({ contact }: { contact: Contact & { organisationNom?: string; taskCount: number } }) {
  const principal = (contact.telephones as any[])?.find((t: any) => t.principal) || (contact.telephones as any[])?.[0];
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-2">
        <Avatar className="h-9 w-9 shrink-0">
          {contact.avatarUrl && <AvatarFallback className={`text-xs font-bold text-white ${avatarColor(contact.prenom + contact.nom)}`} />}
          <AvatarFallback className={`text-xs font-bold text-white ${avatarColor(contact.prenom + contact.nom)}`}>
            {avatarInitials(contact.prenom, contact.nom)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">{contact.prenom} {contact.nom}</p>
          {contact.poste && <p className="text-xs text-muted-foreground truncate">{contact.poste}</p>}
        </div>
      </div>
      <div className="space-y-1 text-xs text-muted-foreground">
        {(contact.organisationNom || contact.organisationLibre) && (
          <div className="flex items-center gap-1"><Building2 className="h-3 w-3 shrink-0" /><span className="truncate">{contact.organisationNom || contact.organisationLibre}</span></div>
        )}
        {contact.email && <div className="flex items-center gap-1"><Mail className="h-3 w-3 shrink-0" /><span className="truncate">{contact.email}</span></div>}
        {principal && <div className="flex items-center gap-1"><Phone className="h-3 w-3 shrink-0" /><span>{principal.numero}</span></div>}
        {contact.taskCount > 0 && (
          <span className="inline-flex items-center gap-1 text-primary font-medium">
            🔗 {contact.taskCount} tâche{contact.taskCount > 1 ? "s" : ""} liée{contact.taskCount > 1 ? "s" : ""}
          </span>
        )}
      </div>
    </div>
  );
}

export default function DuplicatesPage() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  const [detecting, setDetecting] = useState(false);
  const [disqualifiedCount, setDisqualifiedCount] = useState<number | null>(null);

  const { data: candidates = [], isLoading, dataUpdatedAt } = useQuery<Candidate[]>({
    queryKey: ["/api/contacts/duplicates"],
    queryFn: async () => {
      const res = await authFetch("/api/contacts/duplicates?status=pending");
      if (!res.ok) throw new Error("Erreur chargement");
      return res.json();
    },
  });

  const ignoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/contacts/duplicates/${id}/ignore`, { method: "POST" });
      if (!res.ok) throw new Error("Erreur");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/contacts/duplicates"] });
      qc.invalidateQueries({ queryKey: ["/api/contacts/duplicates/count"] });
      toast({ title: "Paire ignorée" });
    },
  });

  const handleDetect = async () => {
    setDetecting(true);
    try {
      const res = await authFetch("/api/contacts/detect-duplicates", { method: "POST" });
      if (!res.ok) throw new Error("Erreur détection");
      const data = await res.json();
      qc.invalidateQueries({ queryKey: ["/api/contacts/duplicates"] });
      qc.invalidateQueries({ queryKey: ["/api/contacts/duplicates/count"] });
      if (data.contactsWithDisqualifiedNames > 0) setDisqualifiedCount(data.contactsWithDisqualifiedNames);
      const disqMsg = data.contactsWithDisqualifiedNames > 0
        ? ` · ${data.contactsWithDisqualifiedNames} fiche${data.contactsWithDisqualifiedNames > 1 ? "s" : ""} à compléter`
        : "";
      toast({ title: `Détection terminée`, description: `${data.detected} paire${data.detected !== 1 ? "s" : ""} détectée${data.detected !== 1 ? "s" : ""}, dont ${data.highConfidence} à score ≥ 90%${disqMsg}` });
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setDetecting(false);
    }
  };

  const scoreColor = (score: number) => {
    if (score >= 95) return "text-red-600 dark:text-red-400";
    if (score >= 85) return "text-amber-600 dark:text-amber-400";
    return "text-blue-600 dark:text-blue-400";
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link href="/contacts">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-1" />Annuaire
          </Button>
        </Link>
      </div>

      {disqualifiedCount !== null && disqualifiedCount > 0 && (
        <Link href="/contacts/admin/a-completer">
          <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50/70 dark:border-amber-800 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300 cursor-pointer hover:bg-amber-100/70 transition-colors">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              <strong>{disqualifiedCount} fiche{disqualifiedCount > 1 ? "s" : ""}</strong> avec un nom non renseigné (ex : "Inconnu") — non détectée{disqualifiedCount > 1 ? "s" : ""} comme doublon. 
              <span className="underline ml-1">Voir et compléter →</span>
            </span>
          </div>
        </Link>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold">Doublons à traiter</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading ? "Chargement..." : candidates.length === 0
              ? "Aucun doublon détecté"
              : `${candidates.length} paire${candidates.length > 1 ? "s" : ""} à traiter`}
            {dataUpdatedAt > 0 && !isLoading && (
              <span className="ml-2 text-xs">
                · Dernière détection {formatDistanceToNow(new Date(dataUpdatedAt), { addSuffix: true, locale: fr })}
              </span>
            )}
          </p>
        </div>
        <Button onClick={handleDetect} disabled={detecting} variant="outline" data-testid="btn-detect-duplicates">
          <RefreshCw className={`h-4 w-4 mr-2 ${detecting ? "animate-spin" : ""}`} />
          {detecting ? "Détection en cours…" : "Relancer la détection"}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <p className="text-muted-foreground">Chargement…</p>
        </div>
      ) : candidates.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-52 gap-4 text-center text-muted-foreground border-2 border-dashed rounded-xl p-8">
          <CheckCircle2 className="h-10 w-10 text-emerald-500" />
          <div>
            <p className="font-medium text-foreground">Aucun doublon détecté 🎉</p>
            <p className="text-sm mt-1">Votre annuaire est propre, ou la détection n'a pas encore été lancée.</p>
          </div>
          <Button onClick={handleDetect} disabled={detecting}>
            <RefreshCw className={`h-4 w-4 mr-2 ${detecting ? "animate-spin" : ""}`} />
            Lancer la détection
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {candidates.map(candidate => {
            const score = Number(candidate.combinedScore);
            return (
              <div key={candidate.id} className="rounded-xl border bg-card shadow-sm p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <ContactMiniCard contact={candidate.contactA} />

                  <div className="flex sm:flex-col items-center justify-center gap-2 px-2">
                    <div className="h-px sm:h-8 sm:w-px w-full sm:w-px bg-border" />
                    <div className="flex flex-col items-center gap-1">
                      <span className={`text-sm font-bold tabular-nums ${scoreColor(score)}`}>{score}%</span>
                      <AlertCircle className={`h-4 w-4 ${scoreColor(score)}`} />
                    </div>
                    <div className="h-px sm:h-8 sm:w-px w-full sm:w-px bg-border" />
                  </div>

                  <ContactMiniCard contact={candidate.contactB} />
                </div>

                <div className="mt-3 pt-3 border-t flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-1.5">
                    {(candidate.detectionReasons || []).map((r, i) => (
                      <ReasonBadge key={i} reason={r} />
                    ))}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-muted-foreground"
                      onClick={() => ignoreMutation.mutate(candidate.id)}
                      disabled={ignoreMutation.isPending}
                      data-testid={`btn-ignore-duplicate-${candidate.id}`}
                    >
                      <EyeOff className="h-3.5 w-3.5 mr-1.5" />Ignorer
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/contacts/fusion?a=${candidate.contactA.id}&b=${candidate.contactB.id}&cid=${candidate.id}`)}
                      data-testid={`btn-merge-duplicate-${candidate.id}`}
                    >
                      <GitMerge className="h-3.5 w-3.5 mr-1.5" />Voir et fusionner
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
